import type { CSSProperties } from 'react'
import type { PlayerColor } from '@/shared/board/types'
import type { TokenPosition } from '@/shared/game/types'

/**
 * Рельса своих фишек: пять кнопок под палец вместо пяти мишеней по 14 пикселей.
 *
 * Зачем. Доска — 25 клеток в ширину, и на телефоне клетка выходит около 14
 * пикселей. Расширенные зоны нажатия (BoardView) помогают попадать, но не
 * помогают ВЫБРАТЬ: своя фишка стоит в куче из четырёх цветов, и чтобы понять,
 * какая из них твоя и по какой есть чем сходить, приходится вглядываться.
 * Рельса отвечает на это списком: вот твои пять, вот те, которыми можно
 * сходить.
 *
 * Доска при этом остаётся рабочей: рельса не отменяет выбор фишки на доске,
 * а дублирует его. Один и тот же выбор двумя путями — это нормально, пока
 * оба ведут в одно состояние (`selected` на экране партии).
 *
 * Рельса принадлежит не «мне», а тому, кто ходит. За одним устройством своего
 * игрока нет вовсе, и фишки в ней меняются вместе с ходом. На чужом ходу она
 * остаётся, но перестаёт быть кнопками: чужими фишками не сходить, а убирать
 * её нельзя — доска центрируется по высоте и прыгала бы на каждом ходу.
 */

/** Где фишка — короткой подписью под кружком. */
const WHERE: Record<TokenPosition['location'], string> = {
  base: 'во дворе',
  ring: 'на доске',
  home: 'дома',
}

type Props = {
  colour: PlayerColor
  /** Позиции фишек того, чей ход, по порядку номеров. */
  tokens: TokenPosition[]
  /** Свои ли это фишки: чужие показываются, но не нажимаются. */
  own: boolean
  /** Номера фишек, которыми сейчас есть чем сходить. */
  movable: number[]
  selected: number | null
  onPick: (token: number) => void
}

export function TokenRail({ colour, tokens, own, movable, selected, onPick }: Props) {
  return (
    <div className="rail" style={{ '--own': `var(--${colour})` } as CSSProperties}>
      {tokens.map((position, token) => {
        const ready = movable.includes(token)
        const marks = [
          'rail__chip',
          ready ? 'rail__chip--ready' : '',
          token === selected ? 'rail__chip--picked' : '',
        ]

        const inside = (
          <>
            <span className="rail__dot" aria-hidden="true" />
            <span className="rail__where">{WHERE[position.location]}</span>
          </>
        )

        // Фишки различаются только номером — другого ключа у них нет.
        const key = `rail-${token}`

        if (!own) {
          return (
            <span className={marks.filter(Boolean).join(' ')} key={key}>
              {inside}
            </span>
          )
        }

        return (
          <button
            key={key}
            className={marks.filter(Boolean).join(' ')}
            type="button"
            /*
             * Неходячие фишки не выключаем: нажатие по ним ничего не портит,
             * а выключенная кнопка выпадает из обхода с клавиатуры и не
             * объясняет, почему по ней нельзя. Ходячие видно подсветкой.
             */
            aria-pressed={token === selected}
            onClick={() => onPick(token)}
          >
            {inside}
          </button>
        )
      })}
    </div>
  )
}
